import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import CountdownTimer from "@/components/countdown-timer";
import { 
  Trophy, 
  TrendingUp, 
  Calendar, 
  ExternalLink, 
  Share2, 
  Clock, 
  Award, 
  Search,
  Filter,
  Star,
  BarChart3,
  User,
  Settings,
  Target,
  Zap,
  Gift,
  Users,
  Hash,
  ArrowUp,
  ArrowDown,
  Eye,
  Play,
  Edit3,
  Save,
  X,
  Mail,
  Phone,
  MapPin,
  Heart,
  CreditCard,
  UserPlus,
  ArrowRight,
  CheckCircle
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
// ChallengeCard component will be defined inline
import SpinnerPopup from "@/components/spinner-popup";
// import ChatBubble from "@/components/ChatBubble";
import { formatSADateTime, formatSADate } from "@shared/timezone";
import { ComponentLoading } from "@/components/ui/loading";
import type { UpdateUserProfile } from "@shared/schema";
import Friends from "@/components/Friends";
import { CountdownTimer as ChallengeCountdownTimer } from "@/components/challenges/CountdownTimer";

// Live Challenges tab removed to eliminate errors

export default function UserDashboard() {
  const { toast } = useToast();
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();
  const isAuthenticated = !!user;
  const isLoading = loading;

  // Auto-fix mobile redirect issues
  useEffect(() => {
    const width = window.innerWidth;
    const forceDesktop = localStorage.getItem("forceDesktop");

    // Auto-clear forceDesktop if on mobile device
    if (width < 768 && forceDesktop === "true") {
      localStorage.removeItem("forceDesktop");
      window.location.href = '/m/dashboard';
    }
  }, []);

  // State for filters and sorting
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  // Auto-join challenge functionality
  useEffect(() => {
    const handleAutoJoinChallenge = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const autoJoinChallengeId = urlParams.get('autoJoinChallenge');

      if (autoJoinChallengeId && user) {
        console.log(`[AUTO-JOIN] Attempting to auto-join challenge ${autoJoinChallengeId}`);

        try {
          // Use apiRequest which handles authentication automatically
          const response = await apiRequest('POST', `/api/v1/challenges/${autoJoinChallengeId}/join`);

          console.log('[AUTO-JOIN] Successfully joined challenge:', response);

          toast({
            title: "🎉 Challenge Joined!",
            description: "You've been automatically entered into the challenge. Start sharing your referral link to compete!",
            duration: 5000
          });

          // Redirect immediately to the challenge page
          window.location.href = `/challenges/${autoJoinChallengeId}`;
        } catch (error: any) {
          console.log('[AUTO-JOIN] Failed to join challenge:', error);

          if (error.message?.includes('Already joined') || error.message?.includes('already participating')) {
            // User is already joined, just redirect to challenge page
            window.location.href = `/challenges/${autoJoinChallengeId}`;
          } else {
            toast({
              title: "Join Failed",
              description: error.message || "Failed to join challenge automatically. You can join manually from the challenge page.",
              variant: "destructive",
              duration: 5000
            });
          }
        }

        // Clean up URL parameter
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('autoJoinChallenge');
        window.history.replaceState({}, '', newUrl.toString());
      }
    };

    // Only run if user is loaded and authenticated
    if (user && !loading) {
      handleAutoJoinChallenge();
    }
  }, [user, loading, toast]);

  // State for profile editing
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState<UpdateUserProfile>({
    firstName: "",
    lastName: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    location: "",
    interests: ""
  });
  const [showFilters, setShowFilters] = useState(false);

  // Spinner popup state
  const [showSpinnerPopup, setShowSpinnerPopup] = useState(false);
  const [hasShownPopupThisSession, setHasShownPopupThisSession] = useState(false);

  // PERFORMANCE: Single optimized dashboard query that includes profile data with improved caching
  const { data: dashboard, isLoading: dashboardLoading } = useQuery<any>({
    queryKey: ["/api/user/dashboard-optimized"],
    staleTime: 30 * 1000, // Cache for 30 seconds for better performance
    refetchOnMount: false, // Don't refetch on every mount to improve performance
    refetchOnWindowFocus: false, // Don't refetch when window gains focus - improves performance
  });

  // Fetch active challenges
  const { data: challengeData, isLoading: challengesLoading } = useQuery({
    queryKey: ['challenges', 'active'],
    queryFn: async () => {
      const response = await fetch('/api/v1/challenges/active');
      if (!response.ok) {
        throw new Error('Failed to fetch challenges');
      }
      return response.json();
    },
    staleTime: 60 * 1000, // Cache for 60 seconds
  });

  // Extract profile from dashboard response to avoid duplicate API call
  const profile = dashboard?.profile as {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string;
    gender?: string;
    location?: string;
    interests?: string;
    idNumber?: string;
  } | undefined;

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (profileData: Partial<UpdateUserProfile>) => {
      return await apiRequest("PUT", "/api/user/profile", { body: profileData });
    },
    onSuccess: () => {
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard-optimized"] });
      setIsEditingProfile(false);
      setProfileFormData({
        firstName: "",
        lastName: "",
        phone: "",
        dateOfBirth: "",
        gender: "",
        location: "",
        interests: ""
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Profile helper functions
  const handleProfileEditToggle = () => {
    if (isEditingProfile) {
      setProfileFormData({
        firstName: "",
        lastName: "",
        phone: "",
        dateOfBirth: "",
        gender: "",
        location: "",
        interests: ""
      });
    } else {
      setProfileFormData({
        firstName: profile?.firstName || "",
        lastName: profile?.lastName || "",
        phone: profile?.phone || "",
        dateOfBirth: profile?.dateOfBirth || "",
        gender: profile?.gender || "",
        location: profile?.location || "",
        interests: profile?.interests || "",
      });
    }
    setIsEditingProfile(!isEditingProfile);
  };

  const handleProfileInputChange = (field: string, value: string) => {
    setProfileFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProfileSubmit = () => {
    updateProfileMutation.mutate(profileFormData);
  };

  // Fetch spinner settings and eligibility
  const { data: spinnerSettings } = useQuery<any>({
    queryKey: ['/api/spinner/settings'],
  });

  const { data: canSpin } = useQuery<any>({
    queryKey: ['/api/spinner/can-spin'],
    enabled: spinnerSettings?.enabled,
  });

  // Remove authentication redirect - let App.tsx routing handle authentication
  // User dashboard will only be rendered for authenticated users via routing

  // Show spinner popup when user can spin (only once per session)
  useEffect(() => {
    if (spinnerSettings?.enabled && canSpin && !showSpinnerPopup && !hasShownPopupThisSession) {
      // Add a small delay to let the dashboard load first
      const timer = setTimeout(() => {
        setShowSpinnerPopup(true);
        setHasShownPopupThisSession(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [spinnerSettings?.enabled, canSpin, showSpinnerPopup, hasShownPopupThisSession]);

  const shareMutation = useMutation({
    mutationFn: async ({ competitionId, platform }: { competitionId: number; platform: string }) => {
      const response = await apiRequest('POST', `/api/competitions/${competitionId}/share`, { platform });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard-optimized"] });
      toast({
        title: "Shared successfully!",
        description: "You've earned a bonus entry for sharing this competition.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error sharing competition",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading || dashboardLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <ComponentLoading text="Loading dashboard..." />
        </div>
        <Footer />
      </div>
    );
  }

  // Remove authentication check - let App.tsx handle routing

  const liveCompetitions = dashboard?.liveCompetitions || [];
  const finishedCompetitions = dashboard?.finishedCompetitions || [];
  const totalEntries = dashboard?.stats?.totalEntries || 0;
  const competitionsEntered = dashboard?.stats?.competitionsEntered || 0;

  // Live challenges data transformation removed
  const finishedCompetitionsCount = dashboard?.stats?.finishedCompetitions || 0;

  // Filter and sort competitions
  const filterCompetitions = (competitions: any[]) => {
    let filtered = competitions;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(comp => 
        comp.competitionTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comp.competitionDescription.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(comp => comp.competitionCategory === categoryFilter);
    }

    // Apply sorting
    switch (sortBy) {
      case "newest":
        filtered.sort((a, b) => new Date(b.competitionEndDate).getTime() - new Date(a.competitionEndDate).getTime());
        break;
      case "oldest":
        filtered.sort((a, b) => new Date(a.competitionEndDate).getTime() - new Date(b.competitionEndDate).getTime());
        break;
      case "most-entries":
        filtered.sort((a, b) => Number(b.totalEntries) - Number(a.totalEntries));
        break;
      case "least-entries":
        filtered.sort((a, b) => Number(a.totalEntries) - Number(b.totalEntries));
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredLiveCompetitions = filterCompetitions(liveCompetitions);
  const filteredFinishedCompetitions = filterCompetitions(finishedCompetitions);

  // Get unique categories for filter dropdown
  const allCategories = [...new Set([...liveCompetitions, ...finishedCompetitions].map(comp => comp.competitionCategory))];

  const handleShare = (competitionId: number, platform: string) => {
    shareMutation.mutate({ competitionId, platform });
  };

  // Challenge handlers removed

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      {/* Dashboard Header - Mobile Responsive */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 leading-tight">
              Welcome back, {profile?.firstName && profile?.lastName ? `${profile.firstName} ${profile.lastName}` : user?.email?.split('@')[0]}!
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-indigo-100 mb-4 sm:mb-6">
              Track your competition entries and history
            </p>

            {spinnerSettings?.enabled && (
              <div className="flex justify-center px-4">
                <Button 
                  onClick={() => setShowSpinnerPopup(true)}
                  size="lg"
                  disabled={!canSpin}
                  className={`w-full sm:w-auto shadow-lg transform hover:scale-105 transition-all duration-300 text-sm sm:text-base ${
                    canSpin 
                      ? 'bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-950 hover:to-blue-900 text-white'
                      : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  <Target className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
                  <span className="truncate">
                    {canSpin ? '🎯 Win Entries Daily Spinner' : '⏰ Come Back Tomorrow'}
                  </span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Dashboard Content - Mobile Responsive */}
      <section className="py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">

          {/* Search and Filter Controls */}
          {false && showFilters && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Filter className="mr-2 h-5 w-5" />
                  Filter & Sort Options
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="search">Search Competitions</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="search"
                        placeholder="Search by title or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {allCategories.map(category => (
                          <SelectItem key={category} value={category}>{category}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="sort">Sort By</Label>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sort by..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="newest">Newest First</SelectItem>
                        <SelectItem value="oldest">Oldest First</SelectItem>
                        <SelectItem value="most-entries">Most Entries</SelectItem>
                        <SelectItem value="least-entries">Least Entries</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setSearchTerm("");
                        setCategoryFilter("all");
                        setSortBy("newest");
                      }}
                      className="w-full"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Competitions Tabs */}
          <Tabs defaultValue="live" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="live" className="flex items-center space-x-1 sm:space-x-2">
                <Zap className="h-4 w-4" />
                <span className="hidden sm:inline">Active Competitions ({liveCompetitions.length})</span>
                <span className="sm:hidden">Active ({liveCompetitions.length})</span>
              </TabsTrigger>
              <TabsTrigger value="challenges" className="flex items-center space-x-1 sm:space-x-2">
                <Trophy className="h-4 w-4" />
                <span className="hidden sm:inline">Challenges ({challengeData?.challenges?.length || 0})</span>
                <span className="sm:hidden">Challenges ({challengeData?.challenges?.length || 0})</span>
              </TabsTrigger>
              <TabsTrigger value="friends" className="flex items-center space-x-1 sm:space-x-2">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Friends</span>
                <span className="sm:hidden">Friends</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="live" className="mt-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold flex items-center">
                    <Zap className="mr-2 h-6 w-6 text-green-600" />
                    Active Competitions
                  </h2>
                  <Badge variant="outline" className="text-green-600">
                    {liveCompetitions.length} Active
                  </Badge>
                </div>
                <p className="text-gray-600">
                  All active competitions available to you. Green badges show competitions you've entered.
                </p>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="mr-2 h-5 w-5" />
                    Available Competitions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {liveCompetitions.length === 0 ? (
                    <div className="text-center py-8">
                      <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">
                        No active competitions available
                      </p>
                      <p className="text-gray-400 text-sm mb-4">
                        Check back later for new competitions!
                      </p>
                      <div className="flex justify-center space-x-2">
                        <Link href="/competitions">
                          <Button>
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Browse Competitions
                          </Button>
                        </Link>

                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {liveCompetitions
                        .sort((a: any, b: any) => {
                          // Sort by active status first (active competitions before scheduled)
                          const now = new Date();

                          const aIsActive = (() => {
                            if (!a.startDate || !a.startTime) return true; // No start date = active
                            const startDateOnly = new Date(a.startDate).toISOString().split('T')[0];
                            const startTimeStr = a.startTime || '00:00';
                            const startDateTime = new Date(`${startDateOnly}T${startTimeStr}:00+02:00`);
                            return startDateTime <= now;
                          })();

                          const bIsActive = (() => {
                            if (!b.startDate || !b.startTime) return true; // No start date = active
                            const startDateOnly = new Date(b.startDate).toISOString().split('T')[0];
                            const startTimeStr = b.startTime || '00:00';
                            const startDateTime = new Date(`${startDateOnly}T${startTimeStr}:00+02:00`);
                            return startDateTime <= now;
                          })();

                          // Active competitions first
                          if (aIsActive && !bIsActive) return -1;
                          if (!aIsActive && bIsActive) return 1;

                          // Within same status, sort by start date/time (earliest first)
                          if (a.startDate && b.startDate) {
                            return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
                          }

                          return 0;
                        })
                        .map((competition: any, index: number) => (
                        <Card key={index} className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = `/competition/${competition.competitionId}`}>
                          <CardContent className="p-4 sm:p-6">
                            <div className="flex flex-col gap-4 mb-4">
                              {/* Mobile Layout - Each element on its own line */}
                              <div className="sm:hidden flex flex-col gap-3">
                                {/* Title */}
                                <div>
                                  <h3 className="font-semibold text-lg">{competition.competitionTitle}</h3>
                                </div>

                                {/* Badges */}
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                    <Zap className="w-3 h-3 mr-1" />
                                    Live
                                  </Badge>
                                  {competition.hasEntered && (
                                    <Badge className="text-xs bg-emerald-100 text-emerald-800 border-emerald-300">
                                      ✓ Entered
                                    </Badge>
                                  )}
                                </div>

                                {/* Timer */}
                                <div>
                                  {(() => {
                                    try {
                                      const now = new Date();
                                      if (competition.startDate && competition.startTime) {
                                        const startDateOnly = new Date(competition.startDate).toISOString().split('T')[0];
                                        const startTimeStr = competition.startTime || '00:00';
                                        const startDateTime = new Date(`${startDateOnly}T${startTimeStr}:00+02:00`);
                                        const isScheduled = startDateTime > now;

                                        if (isScheduled) {
                                          return <CountdownTimer endDate={competition.startDate} endTime={competition.startTime} compact />;
                                        }
                                      }
                                      return <CountdownTimer endDate={competition.competitionEndDate} endTime={competition.competitionEndTime} compact />;
                                    } catch (error) {
                                      return <CountdownTimer endDate={competition.competitionEndDate} endTime={competition.competitionEndTime} compact />;
                                    }
                                  })()}
                                </div>
                              </div>

                              {/* Desktop Layout - Original layout */}
                              <div className="hidden sm:flex flex-row justify-between items-start gap-4">
                                <div className="flex-1">
                                  <div className="flex flex-row items-center space-x-2 mb-2">
                                    <h3 className="font-semibold text-lg">{competition.competitionTitle}</h3>
                                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                      <Zap className="w-3 h-3 mr-1" />
                                      Live
                                    </Badge>
                                    {competition.hasEntered && (
                                      <Badge className="text-xs bg-emerald-100 text-emerald-800 border-emerald-300">
                                        ✓ Entered
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="flex-shrink-0">
                                  {(() => {
                                    try {
                                      const now = new Date();
                                      if (competition.startDate && competition.startTime) {
                                        const startDateOnly = new Date(competition.startDate).toISOString().split('T')[0];
                                        const startTimeStr = competition.startTime || '00:00';
                                        const startDateTime = new Date(`${startDateOnly}T${startTimeStr}:00+02:00`);
                                        const isScheduled = startDateTime > now;

                                        if (isScheduled) {
                                          return <CountdownTimer endDate={competition.startDate} endTime={competition.startTime} compact />;
                                        }
                                      }
                                      return <CountdownTimer endDate={competition.competitionEndDate} endTime={competition.competitionEndTime} compact />;
                                    } catch (error) {
                                      return <CountdownTimer endDate={competition.competitionEndDate} endTime={competition.competitionEndTime} compact />;
                                    }
                                  })()}
                                </div>
                              </div>

                              {/* Competition info - Mobile: separate lines, Desktop: same line */}
                              <div className="sm:hidden flex flex-col gap-3">
                                {/* Category and Type badges on mobile */}
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge variant="outline">{competition.competitionCategory}</Badge>
                                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                                    <Clock className="w-3 h-3 mr-1" />
                                    {(() => {
                                      // First check if competition has a type field
                                      if (competition.competitionType) {
                                        return competition.competitionType.charAt(0).toUpperCase() + competition.competitionType.slice(1);
                                      }

                                      // Fallback to title/description analysis
                                      const title = competition.competitionTitle.toLowerCase();
                                      const description = competition.competitionDescription.toLowerCase();

                                      if (title.includes('daily') || description.includes('daily')) return 'Daily';
                                      if (title.includes('weekly') || description.includes('weekly') || title.includes('week')) return 'Weekly';
                                      if (title.includes('monthly') || description.includes('monthly') || title.includes('month')) return 'Monthly';

                                      // If no specific type found, determine by prize value or indicators
                                      const prize = competition.competitionPrize.toLowerCase();
                                      if (prize.includes('$100') || prize.includes('cash')) return 'Daily';
                                      if (prize.includes('$3000') || prize.includes('gaming') || prize.includes('setup')) return 'Monthly';
                                      if (prize.includes('iphone') || prize.includes('pro')) return 'Weekly';

                                      return 'Daily'; // Default to Daily for most competitions
                                    })()}
                                  </Badge>
                                </div>

                                {/* Prize section - stacked format using separate fields */}
                                <div className="space-y-2">
                                  {competition.competitionPrize1 && (
                                    <div className="flex items-start">
                                      <span className="text-sm font-medium text-gray-900">{competition.competitionPrize1}</span>
                                    </div>
                                  )}
                                  {competition.competitionPrize2 && (
                                    <div className="flex items-start">
                                      <span className="text-sm font-medium text-gray-900">{competition.competitionPrize2}</span>
                                    </div>
                                  )}
                                  {competition.competitionPrize3 && (
                                    <div className="flex items-start">
                                      <span className="text-sm font-medium text-gray-900">{competition.competitionPrize3}</span>
                                    </div>
                                  )}
                                  {/* Fallback for backward compatibility with old prize field */}
                                  {!competition.competitionPrize1 && !competition.competitionPrize2 && !competition.competitionPrize3 && competition.competitionPrize && (
                                    <div className="flex items-start">
                                      <span className="text-sm font-medium text-gray-900">{competition.competitionPrize}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Desktop layout - all on same line */}
                              <div className="hidden sm:flex items-center space-x-4 text-sm text-gray-600">
                                <Badge variant="outline">{competition.competitionCategory}</Badge>
                                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {(() => {
                                    // First check if competition has a type field
                                    if (competition.competitionType) {
                                      return competition.competitionType.charAt(0).toUpperCase() + competition.competitionType.slice(1);
                                    }

                                    // Fallback to title/description analysis
                                    const title = competition.competitionTitle.toLowerCase();
                                    const description = competition.competitionDescription.toLowerCase();

                                    if (title.includes('daily') || description.includes('daily')) return 'Daily';
                                    if (title.includes('weekly') || description.includes('weekly') || title.includes('week')) return 'Weekly';
                                    if (title.includes('monthly') || description.includes('monthly') || title.includes('month')) return 'Monthly';

                                    // If no specific type found, determine by prize value or indicators
                                    const prize = competition.competitionPrize.toLowerCase();
                                    if (prize.includes('$100') || prize.includes('cash')) return 'Daily';
                                    if (prize.includes('$3000') || prize.includes('gaming') || prize.includes('setup')) return 'Monthly';
                                    if (prize.includes('iphone') || prize.includes('pro')) return 'Weekly';

                                    return 'Daily'; // Default to Daily for most competitions
                                  })()}
                                </Badge>
                                <div className="space-y-1">
                                  {(() => {
                                    const prizeText = competition.competitionPrize || '';
                                    // Try multiple separators to split prizes: newlines, commas, or "R" pattern
                                    let prizes = [];

                                    if (prizeText.includes('\n')) {
                                      prizes = prizeText.split(/\n/).filter((p: string) => p.trim());
                                    } else if (prizeText.includes(',')) {
                                      prizes = prizeText.split(',').filter((p: string) => p.trim());
                                    } else {
                                      // Split on "R" followed by number (like R25, R50, etc.) but keep the R
                                      prizes = prizeText.split(/(?=R\d+)/).filter((p: string) => p.trim());
                                    }

                                    // If still no splits, just show the whole text
                                    if (prizes.length === 0) {
                                      prizes = [prizeText];
                                    }

                                    // Limit to only 3 prizes for desktop too
                                    const limitedPrizes = prizes.slice(0, 3);

                                    return limitedPrizes.map((prize: string, index: number) => {
                                      const trimmedPrize = prize.trim();
                                      let icon = null;

                                      // Determine icon based on prize content
                                      if (trimmedPrize.toLowerCase().includes('betfred')) {
                                        icon = (
                                          <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" style={{background: 'linear-gradient(135deg, #e0304d, #21599a)'}}>
                                            <div className="text-white font-bold text-xs">B</div>
                                          </div>
                                        );
                                      } else {
                                        // Default gift icon for all other prizes
                                        icon = <Gift className="w-4 h-4 text-primary flex-shrink-0" />;
                                      }

                                      return (
                                        <div key={index} className="flex items-center gap-2">
                                          {icon}
                                          <span className="text-sm font-medium text-gray-900">{trimmedPrize}</span>
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>
                            </div>


{!competition.hasEntered && (
                              <div className="text-center p-6 bg-gray-50 rounded-lg mb-4">
                                {(() => {
                                  try {
                                    const now = new Date();
                                    if (competition.startDate && competition.startTime) {
                                      const startDateOnly = new Date(competition.startDate).toISOString().split('T')[0];
                                      const startTimeStr = competition.startTime || '00:00';
                                      const startDateTime = new Date(`${startDateOnly}T${startTimeStr}:00+02:00`);
                                      const isScheduled = startDateTime > now;

                                      if (isScheduled) {
                                        return (
                                          <div className="text-gray-600 mb-2">
                                            <Clock className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                                            <div className="font-medium">Competition Scheduled</div>
                                            <div className="text-sm">This competition will start soon. Come back when it begins!</div>
                                          </div>
                                        );
                                      }
                                    }
                                    // Default message for active competitions
                                    return (
                                      <div className="text-gray-600 mb-2">
                                        <Trophy className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                                        <div className="font-medium">Ready to Enter</div>
                                        <div className="text-sm">Answer {competition.totalQuestions} questions to start earning entries</div>
                                      </div>
                                    );
                                  } catch (error) {
                                    return (
                                      <div className="text-gray-600 mb-2">
                                        <Trophy className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                                        <div className="font-medium">Ready to Enter</div>
                                        <div className="text-sm">Answer {competition.totalQuestions} questions to start earning entries</div>
                                      </div>
                                    );
                                  }
                                })()}
                              </div>
                            )}

                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-2">
                                <Badge variant="secondary" className="text-xs">
                                  <Hash className="w-3 h-3 mr-1" />
                                  Entry #{competition.competitionId}
                                </Badge>
{(() => {
                                  try {
                                    const now = new Date();
                                    if (competition.startDate && competition.startTime) {
                                      const startDateOnly = new Date(competition.startDate).toISOString().split('T')[0];
                                      const startTimeStr = competition.startTime || '00:00';
                                      const startDateTime = new Date(`${startDateOnly}T${startTimeStr}:00+02:00`);
                                      const isScheduled = startDateTime > now;

                                      if (isScheduled) {
                                        return (
                                          <Badge variant="outline" className="text-xs text-orange-600">
                                            <Clock className="w-3 h-3 mr-1" />
                                            Scheduled
                                          </Badge>
                                        );
                                      }
                                    }
                                    // Default to Active for current competitions
                                    return (
                                      <Badge variant="outline" className="text-xs text-green-600">
                                        <ArrowUp className="w-3 h-3 mr-1" />
                                        Active
                                      </Badge>
                                    );
                                  } catch (error) {
                                    return (
                                      <Badge variant="outline" className="text-xs text-green-600">
                                        <ArrowUp className="w-3 h-3 mr-1" />
                                        Active
                                      </Badge>
                                    );
                                  }
                                })()}
                              </div>
                            </div>


                            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-2">
                              {competition.hasEntered ? (
                                <>
                                  {Math.max(0, (Number(competition.totalQuestions) || 0) - Number(competition.answerEntries)) > 0 && (
                                    <Link href={`/competition/${competition.competitionId}`}>
                                      <Button size="sm" className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white">
                                        <Play className="h-4 w-4 mr-2" />
                                        <span className="hidden sm:inline">Continue ({Math.max(0, (Number(competition.totalQuestions) || 0) - Number(competition.answerEntries))} left)</span>
                                        <span className="sm:hidden">Continue ({Math.max(0, (Number(competition.totalQuestions) || 0) - Number(competition.answerEntries))})</span>
                                      </Button>
                                    </Link>
                                  )}
                                  <Link href={`/competition/${competition.competitionId}`}>
                                    <Button size="sm" className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white">
                                      <Eye className="h-4 w-4 mr-2" />
                                      <span className="hidden sm:inline">View Progress</span>
                                      <span className="sm:hidden">Progress</span>
                                    </Button>
                                  </Link>
                                </>
                              ) : (
                                <>
                                  {(() => {
                                    // Check if competition is scheduled for future
                                    try {
                                      const now = new Date();
                                      if (!competition.startDate) {
                                        // No start date, check if user has entered
                                        const hasAnsweredQuestions = Number(competition.answerEntries) > 0;
                                        const remainingQuestions = Math.max(0, (Number(competition.totalQuestions) || 0) - Number(competition.answerEntries));

                                        if (hasAnsweredQuestions && remainingQuestions > 0) {
                                          return (
                                            <Link href={`/competition/${competition.competitionId}`}>
                                              <Button size="sm" className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white">
                                                <Play className="h-4 w-4 mr-2" />
                                                <span className="hidden sm:inline">Continue ({remainingQuestions} left)</span>
                                                <span className="sm:hidden">Continue ({remainingQuestions})</span>
                                              </Button>
                                            </Link>
                                          );
                                        } else if (hasAnsweredQuestions && remainingQuestions === 0) {
                                          return (
                                            <Link href={`/competition/${competition.competitionId}`}>
                                              <Button size="sm" className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white">
                                                <Eye className="h-4 w-4 mr-2" />
                                                View Details
                                              </Button>
                                            </Link>
                                          );
                                        } else {
                                          return (
                                            <Link href={`/competition/${competition.competitionId}`}>
                                              <Button size="sm" className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white">
                                                <Trophy className="h-4 w-4 mr-2" />
                                                Enter Now
                                              </Button>
                                            </Link>
                                          );
                                        }
                                      }

                                      const startDateOnly = new Date(competition.startDate).toISOString().split('T')[0];
                                      const startTimeStr = competition.startTime || '00:00';
                                      const startDateTime = new Date(`${startDateOnly}T${startTimeStr}:00+02:00`);
                                      const isScheduled = startDateTime > now;

                                      if (isScheduled) {
                                        return (
                                          <Button 
                                            size="sm" 
                                            className="w-full sm:w-auto bg-gray-400 hover:bg-gray-400 text-white cursor-not-allowed"
                                            disabled
                                          >
                                            <Clock className="h-4 w-4 mr-2" />
                                            Starts Soon
                                          </Button>
                                        );
                                      } else {
                                        // Check if user has already entered competition
                                        const hasAnsweredQuestions = Number(competition.answerEntries) > 0;
                                        const remainingQuestions = Math.max(0, (Number(competition.totalQuestions) || 0) - Number(competition.answerEntries));

                                        if (hasAnsweredQuestions && remainingQuestions > 0) {
                                          return (
                                            <Link href={`/competition/${competition.competitionId}`}>
                                              <Button size="sm" className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white">
                                                <Play className="h-4 w-4 mr-2" />
                                                <span className="hidden sm:inline">Continue ({remainingQuestions} left)</span>
                                                <span className="sm:hidden">Continue ({remainingQuestions})</span>
                                              </Button>
                                            </Link>
                                          );
                                        } else if (hasAnsweredQuestions && remainingQuestions === 0) {
                                          return (
                                            <Link href={`/competition/${competition.competitionId}`}>
                                              <Button size="sm" className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white">
                                                <Eye className="h-4 w-4 mr-2" />
                                                View Details
                                              </Button>
                                            </Link>
                                          );
                                        } else {
                                          return (
                                            <Link href={`/competition/${competition.competitionId}`}>
                                              <Button size="sm" className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white">
                                                <Trophy className="h-4 w-4 mr-2" />
                                                Enter Now
                                              </Button>
                                            </Link>
                                          );
                                        }
                                      }
                                    } catch (error) {
                                      // If date parsing fails, check if user has entered
                                      const hasAnsweredQuestions = Number(competition.answerEntries) > 0;
                                      const remainingQuestions = Math.max(0, (Number(competition.totalQuestions) || 0) - Number(competition.answerEntries));

                                      if (hasAnsweredQuestions && remainingQuestions > 0) {
                                        return (
                                          <Link href={`/competition/${competition.competitionId}`}>
                                            <Button size="sm" className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white">
                                              <Play className="h-4 w-4 mr-2" />
                                              <span className="hidden sm:inline">Continue ({remainingQuestions} left)</span>
                                              <span className="sm:hidden">Continue ({remainingQuestions})</span>
                                            </Button>
                                          </Link>
                                        );
                                      } else if (hasAnsweredQuestions && remainingQuestions === 0) {
                                        return (
                                          <Link href={`/competition/${competition.competitionId}`}>
                                            <Button size="sm" className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white">
                                              <Eye className="h-4 w-4 mr-2" />
                                              View Details
                                            </Button>
                                          </Link>
                                        );
                                      } else {
                                        return (
                                          <Link href={`/competition/${competition.competitionId}`}>
                                            <Button size="sm" className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white">
                                              <Trophy className="h-4 w-4 mr-2" />
                                              Enter Now
                                            </Button>
                                          </Link>
                                        );
                                      }
                                    }
                                  })()}
                                  <Link href={`/competition/${competition.competitionId}`}>
                                    <Button size="sm" variant="outline" className="w-full sm:w-auto">
                                      <Eye className="h-4 w-4 mr-2" />
                                      <span className="hidden sm:inline">View Details</span>
                                      <span className="sm:hidden">Details</span>
                                    </Button>
                                  </Link>
                                </>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>


            {/* HIDDEN: Competition History Content - keeping in code but commented out */}
            {/* <TabsContent value="finished" className="mt-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold flex items-center">
                    <Award className="mr-2 h-6 w-6 text-blue-900" />
                    Competition History
                  </h2>
                  <Badge variant="outline" className="text-blue-900">
                    {finishedCompetitions.length} Completed
                  </Badge>
                </div>
                <p className="text-gray-600">
                  Here's your complete competition history showing results, entries, and any prizes won.
                </p>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Award className="mr-2 h-5 w-5" />
                    Your Completed Competitions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {finishedCompetitions.length === 0 ? (
                    <div className="text-center py-8">
                      <Award className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">
                        No finished competitions yet
                      </p>
                      <p className="text-gray-400 text-sm mb-4">
                        Your completed competitions will appear here
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {finishedCompetitions.map((competition: any, index: number) => (
                        <Card key={index} className="border-l-4 border-l-gray-400 hover:shadow-lg transition-shadow">
                          <CardContent className="p-3 sm:p-4">
                            <div className="space-y-3">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 mb-2">
                                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">{competition.competitionTitle}</h3>
                                    <Badge variant="outline" className="text-xs bg-gray-50 text-gray-700 border-gray-200 w-fit">
                                      <ArrowDown className="w-3 h-3 mr-1" />
                                      Ended
                                    </Badge>
                                  </div>

                                  <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-600">
                                    <Badge variant="secondary" className="text-xs">{competition.competitionCategory}</Badge>
                                    <Badge variant="outline" className="bg-gray-100 text-gray-700 text-xs">
                                      <Clock className="w-3 h-3 mr-1" />
                                      {(() => {
                                        // First check if competition has a type field
                                        if (competition.competitionType) {
                                          return competition.competitionType.charAt(0).toUpperCase() + competition.competitionType.slice(1);
                                        }

                                        // Fallback to title/description analysis
                                        const title = competition.competitionTitle.toLowerCase();
                                        const description = competition.competitionDescription.toLowerCase();

                                        if (title.includes('daily') || description.includes('daily')) return 'Daily';
                                        if (title.includes('weekly') || description.includes('weekly') || title.includes('week')) return 'Weekly';
                                        if (title.includes('monthly') || description.includes('monthly') || title.includes('month')) return 'Monthly';

                                        // If no specific type found, determine by prize value or indicators
                                        const prize = competition.competitionPrize.toLowerCase();
                                        if (prize.includes('$100') || prize.includes('cash')) return 'Daily';
                                        if (prize.includes('$3000') || prize.includes('gaming') || prize.includes('setup')) return 'Monthly';
                                        if (prize.includes('iphone') || prize.includes('pro')) return 'Weekly';

                                        return 'Daily'; // Default to Daily for most competitions
                                      })()}
                                    </Badge>
                                    <div className="flex flex-col gap-1">
                                      {(() => {
                                        const prizes = competition.competitionPrize.split(/(?=R\d+)/).filter(p => p.trim());
                                        return prizes.map((prize, idx) => (
                                          <div key={idx} className="flex items-center text-xs sm:text-sm">
                                            <Gift className="w-3 h-3 mr-1" />
                                            <span className="font-medium truncate">{prize.trim()}</span>
                                          </div>
                                        ));
                                      })()}
                                    </div>
                                    <div className="flex items-center text-xs sm:text-sm">
                                      <Calendar className="w-3 h-3 mr-1" />
                                      <span>Ended: {new Date(competition.competitionEndDate).toLocaleDateString()}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>


                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-center space-x-2">
                                  <Badge variant="secondary" className="text-xs">
                                    <Hash className="w-3 h-3 mr-1" />
                                    Entry #{competition.competitionId}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs text-gray-600">
                                    <Star className="w-3 h-3 mr-1" />
                                    Completed
                                  </Badge>
                                </div>
                                <Link href={`/competition/${competition.competitionId}`}>
                                  <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white w-full sm:w-auto">
                                    <Eye className="h-4 w-4 mr-2" />
                                    View Progress
                                  </Button>
                                </Link>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent> */}

            {/* Live Challenges tab removed */}

            <TabsContent value="challenges" className="mt-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold flex items-center">
                    <Trophy className="mr-2 h-6 w-6 text-purple-600" />
                    Challenges
                  </h2>
                </div>
                <p className="text-gray-600">
                  Join exciting referral challenges and compete for amazing prizes! Refer friends and climb the leaderboard.
                </p>
              </div>

              {challengesLoading ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading challenges...</p>
                  </div>
                </div>
              ) : challengeData?.challenges && challengeData.challenges.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {challengeData.challenges.map((challenge: any) => (
                    <Card key={challenge.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-xl mb-2">{challenge.name}</CardTitle>
                            <Badge variant={challenge.status === 'live' ? 'default' : 'secondary'}>
                              {challenge.status === 'live' ? 'Active' : challenge.status}
                            </Badge>
                          </div>
                          <Trophy className="h-8 w-8 text-yellow-500" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {challenge.description && (
                            <p className="text-sm text-gray-600">{challenge.description}</p>
                          )}

                          {/* Prize Details */}
                          {challenge.prizeDetails && (
                            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-3 rounded-lg">
                              <p className="text-xs font-semibold text-gray-700 mb-2">Prizes:</p>
                              <div className="space-y-1">
                                {challenge.prizeDetails['1st'] && (
                                  <div className="flex items-center text-sm">
                                    <Trophy className="h-4 w-4 text-yellow-500 mr-2" />
                                    <span className="font-medium">1st:</span>
                                    <span className="ml-1">{challenge.prizeDetails['1st'].item}</span>
                                  </div>
                                )}
                                {challenge.prizeDetails['2nd'] && (
                                  <div className="flex items-center text-sm text-gray-700">
                                    <Trophy className="h-4 w-4 text-gray-400 mr-2" />
                                    <span className="font-medium">2nd:</span>
                                    <span className="ml-1">{challenge.prizeDetails['2nd'].item}</span>
                                  </div>
                                )}
                                {challenge.prizeDetails['3rd'] && (
                                  <div className="flex items-center text-sm text-gray-700">
                                    <Trophy className="h-4 w-4 text-orange-400 mr-2" />
                                    <span className="font-medium">3rd:</span>
                                    <span className="ml-1">{challenge.prizeDetails['3rd'].item}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Stats */}
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center text-gray-600">
                              <Users className="h-4 w-4 mr-1" />
                              <span>{challenge.totalParticipants} participants</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Target className="h-4 w-4 mr-1" />
                              <span>{challenge.targetValue} target</span>
                            </div>
                          </div>

                          {/* Action Button */}
                          <Link href={`/challenges/${challenge.id}`}>
                            <Button className="w-full">
                              View Challenge
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="text-center py-16">
                    <Trophy className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">No Challenges Available</h3>
                    <p className="text-gray-600 mb-6">
                      There are currently no challenges available. Check back soon for new competitions!
                    </p>
                    <Link href="/competitions">
                      <Button>
                        View Competitions Instead
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="friends" className="mt-6">
              <Friends />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />

      {/* Spinner Popup */}
      <SpinnerPopup 
        isOpen={showSpinnerPopup} 
        onClose={() => setShowSpinnerPopup(false)} 
      />

      {/* Chat Support Bubble */}
      {/* <ChatBubble /> */}
    </div>
  );
}
